import { AlertTriangle, Clock, FolderOpen, OctagonAlert, Plus, RefreshCw, RotateCcw } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { useSearchParams } from '@/lib/scopedParams';
import { api, type AbuseCase, type AbuseCaseStatus, type NewAbuseReport } from '@/api';
import { DataError } from '@/components/DataError';
import { DataTable, Field, FilterBar, Section, StatCard, StatGrid, type Column } from '@/components/blocks';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { formatCount, formatRelative } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { ActionDialog, DueCell, EnumBadge, FormActions, FormGrid, overdueFirst, SelectField, useEnumLabel, useEnumOptions, When } from './ui';
import { ABUSE_CATEGORIES, ABUSE_CHANNELS, ABUSE_PRIORITIES, ABUSE_SOURCES, ABUSE_STATUS, PRIORITY, REPORTER_TYPES } from './vocab';

type AbuseCaseRow = AbuseCase;

function RegisterReportForm({ onCancel }: { onCancel: () => void }) {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const options = useEnumOptions();
  const navigate = useNavigate();
  const [f, setF] = useState<NewAbuseReport>({
    domain: '',
    category: 'PHISHING',
    priority: 'HIGH',
    source: 'REPORT',
    channel: 'WEB_FORM',
    reporter_type: 'ORGANIZATION',
    reporter_contact: '',
    summary: '',
    external_reference: '',
  });
  const write = useWrite((req: NewAbuseReport, opts) => api.createAbuseCase(req, opts));
  const set =
    <K extends keyof NewAbuseReport>(k: K) =>
    (v: NewAbuseReport[K]) =>
      setF({ ...f, [k]: v });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await write.run({
      ...f,
      domain: f.domain.trim(),
      summary: f.summary.trim(),
      reporter_contact: f.reporter_contact?.trim() || undefined,
      external_reference: f.external_reference?.trim() || null,
    });
    if (res) navigate(`/compliance/icann/abuse/${encodeURIComponent(res.case_id)}${res.created ? '' : '?attached=1'}`);
  };

  return (
    <form onSubmit={submit} className="grid gap-4">
      <FormGrid>
        <Field label={t('abuse.form.domain')} className="sm:col-span-2">
          <Input value={f.domain} onChange={(e) => set('domain')(e.target.value)} required disabled={write.busy} spellCheck={false} placeholder="example.vn" />
        </Field>
        <SelectField
          label={t('abuse.form.category')}
          value={f.category}
          options={options('abuseCategory', ABUSE_CATEGORIES)}
          onChange={(v) => v && set('category')(v)}
          disabled={write.busy}
        />
        <SelectField
          label={t('abuse.form.priority')}
          value={f.priority}
          options={options('priority', ABUSE_PRIORITIES)}
          onChange={(v) => v && set('priority')(v)}
          disabled={write.busy}
        />
        <SelectField
          label={t('abuse.form.source')}
          value={f.source}
          options={options('abuseSource', ABUSE_SOURCES)}
          onChange={(v) => v && set('source')(v)}
          disabled={write.busy}
        />
        <SelectField
          label={t('abuse.form.channel')}
          value={f.channel}
          options={options('channel', ABUSE_CHANNELS)}
          onChange={(v) => v && set('channel')(v)}
          disabled={write.busy}
        />
        <SelectField
          label={t('abuse.form.reporterType')}
          value={f.reporter_type}
          options={options('reporterType', REPORTER_TYPES)}
          onChange={(v) => v && set('reporter_type')(v)}
          disabled={write.busy}
        />
        <Field label={t('abuse.form.reporterContact')}>
          <Input value={f.reporter_contact ?? ''} onChange={(e) => set('reporter_contact')(e.target.value)} disabled={write.busy} autoComplete="off" />
        </Field>
        <Field label={t('abuse.form.externalRef')} className="sm:col-span-2">
          <Input value={f.external_reference ?? ''} onChange={(e) => set('external_reference')(e.target.value)} disabled={write.busy} />
        </Field>
        <Field label={t('abuse.form.summary')} className="sm:col-span-2">
          <Textarea rows={3} value={f.summary} onChange={(e) => set('summary')(e.target.value)} required maxLength={4000} disabled={write.busy} />
        </Field>
      </FormGrid>
      {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel} disabled={write.busy}>
          {tc('action.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy || !f.domain.trim() || !f.summary.trim()}>
          {write.busy ? t('abuse.form.busy') : t('abuse.form.submit')}
        </Button>
      </FormActions>
    </form>
  );
}

/** `/compliance/icann/abuse`: abuse case queue ordered by first-response deadline, overdue first (spec §44). */
export function AbuseCasesPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const options = useEnumOptions();
  useDocumentTitle(t('abuse.list.title'));
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as AbuseCaseStatus | '';
  const category = (params.get('category') ?? '') as NewAbuseReport['category'] | '';
  const domain = params.get('domain') ?? '';
  const overdue = params.get('overdue') === '1';
  const [creating, setCreating] = useState(false);
  const result = useApi(
    (signal) =>
      api.listAbuseCases(
        {
          status: status || undefined,
          category: category || undefined,
          domain: domain || undefined,
          overdue,
          limit: 200,
        },
        { signal },
      ),
    [status, category, domain, overdue],
  );

  const set = (k: string, v: string) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v);
    else p.delete(k);
    setParams(p);
  };
  const filtered = Boolean(status || category || domain || overdue);

  const items = result.status === 'success' ? overdueFirst(result.data.items, (c) => Boolean(c.overdue) && !c.first_responded_at) : [];
  const open = items.filter((c) => c.status !== 'CLOSED' && c.status !== 'RESOLVED');
  const overdueCount = items.filter((c) => Boolean(c.overdue) && !c.first_responded_at).length;

  const columns: Column<AbuseCaseRow>[] = [
    {
      key: 'case',
      header: t('abuse.list.col.case'),
      cell: (c) => (
        <Link to={`/compliance/icann/abuse/${encodeURIComponent(c.id)}`} className="font-mono text-sm font-semibold text-primary hover:underline">
          {c.case_number}
        </Link>
      ),
    },
    {
      key: 'domain',
      header: t('abuse.list.col.domain'),
      cell: (c) =>
        c.domain_id ? (
          <Link to={`/domains/${encodeURIComponent(c.reported_name)}`} className="hover:underline">
            {c.reported_name}
          </Link>
        ) : (
          <span title={t('common.notManaged')}>{c.reported_name}</span>
        ),
    },
    {
      key: 'category',
      header: t('abuse.list.col.category'),
      cell: (c) => <span title={c.category}>{label('abuseCategory', c.category)}</span>,
    },
    {
      key: 'priority',
      header: t('abuse.list.col.priority'),
      cell: (c) => <EnumBadge group="priority" value={c.priority} tones={PRIORITY} />,
    },
    {
      key: 'status',
      header: t('abuse.list.col.status'),
      cell: (c) => <EnumBadge group="abuseStatus" value={c.status} tones={ABUSE_STATUS} />,
    },
    {
      key: 'due',
      header: t('abuse.list.col.firstResponse'),
      cell: (c) => (
        <DueCell
          dueAt={c.first_response_due_at}
          overdue={Boolean(c.overdue)}
          policyMissing={c.sla_status === 'POLICY_MISSING'}
          done={
            c.first_responded_at
              ? t('sla.responded', {
                  when: formatRelative(c.first_responded_at),
                })
              : undefined
          }
        />
      ),
    },
    {
      key: 'reports',
      header: t('abuse.list.col.reports'),
      numeric: true,
      cell: (c) => c.report_count ?? '—',
    },
    {
      key: 'opened',
      header: t('abuse.list.col.opened'),
      cell: (c) => <When at={c.opened_at} />,
    },
  ];

  return (
    <>
      <PageHeader
        title={t('abuse.list.title')}
        crumbs={[{ label: t('crumbs.compliance') }, { label: t('crumbs.icann') }]}
        subtitle={t('abuse.list.subtitle')}
        actions={
          <>
            <Button size="sm" onClick={() => setCreating(true)}>
              <Plus /> {t('abuse.list.register')}
            </Button>
            <Button size="sm" variant="outline" onClick={result.reload}>
              <RefreshCw /> {tc('action.refresh')}
            </Button>
          </>
        }
      />
      <ActionDialog open={creating} onOpenChange={setCreating} title={t('abuse.form.title')} description={t('abuse.form.description')} wide>
        <RegisterReportForm onCancel={() => setCreating(false)} />
      </ActionDialog>

      <div className="space-y-6">
        {result.status === 'success' ? (
          <StatGrid>
            <StatCard label={t('abuse.list.stats.open')} value={formatCount(open.length)} icon={FolderOpen} />
            <StatCard
              label={t('abuse.list.stats.overdue')}
              value={formatCount(overdueCount)}
              tone={overdueCount > 0 ? 'crit' : 'ok'}
              status={overdueCount > 0 ? t('sla.overdue') : t('abuse.list.stats.allOnTime')}
              hint={overdueCount > 0 ? t('abuse.list.stats.overdueHint') : undefined}
              icon={Clock}
            />
            <StatCard
              label={t('abuse.list.stats.actionRequired')}
              value={formatCount(items.filter((c) => c.status === 'ACTION_REQUIRED').length)}
              tone={items.some((c) => c.status === 'ACTION_REQUIRED') ? 'serious' : 'neutral'}
              icon={AlertTriangle}
            />
            <StatCard
              label={t('abuse.list.stats.critical')}
              value={formatCount(open.filter((c) => c.priority === 'CRITICAL').length)}
              tone={open.some((c) => c.priority === 'CRITICAL') ? 'crit' : 'neutral'}
              icon={OctagonAlert}
            />
          </StatGrid>
        ) : null}

        <Section flush>
          <FilterBar
            className="px-5"
            actions={
              filtered ? (
                <Button variant="ghost" size="sm" onClick={() => setParams(new URLSearchParams())}>
                  <RotateCcw /> {tc('action.reset')}
                </Button>
              ) : null
            }
          >
            <SelectField
              label={t('abuse.list.filter.status')}
              value={status}
              allLabel={t('common.any')}
              options={options('abuseStatus', Object.keys(ABUSE_STATUS) as AbuseCaseStatus[])}
              onChange={(v) => set('status', v)}
              className="w-full sm:w-48"
            />
            <SelectField
              label={t('abuse.list.filter.category')}
              value={category}
              allLabel={t('common.any')}
              options={options('abuseCategory', ABUSE_CATEGORIES)}
              onChange={(v) => set('category', v)}
              className="w-full sm:w-44"
            />
            <Field label={t('abuse.list.filter.domain')} className="w-full sm:w-56">
              <Input
                key={domain}
                type="search"
                defaultValue={domain}
                onKeyDown={(e) => e.key === 'Enter' && set('domain', e.currentTarget.value.trim())}
                onBlur={(e) => set('domain', e.target.value.trim())}
                placeholder={t('common.exactName')}
              />
            </Field>
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- the checkbox is the child */}
            <label className="flex h-9 items-center gap-2 text-sm">
              <Checkbox checked={overdue} onCheckedChange={(v) => set('overdue', v === true ? '1' : '')} />
              {t('abuse.list.overdueOnly')}
            </label>
          </FilterBar>
          {result.status === 'loading' ? (
            <div className="px-5">
              <Loading rows={5} />
            </div>
          ) : null}
          {result.status === 'error' ? (
            <div className="px-5 pb-5">
              <DataError error={result.error} onRetry={result.reload} title={t('abuse.list.loadError')} />
            </div>
          ) : null}
          {result.status === 'success' ? (
            <DataTable
              columns={columns}
              rows={items}
              rowKey={(c) => c.id}
              empty={<EmptyState title={t('abuse.list.empty')}>{filtered ? t('abuse.list.emptyHint') : null}</EmptyState>}
            />
          ) : null}
        </Section>
      </div>
    </>
  );
}
